import Phaser from 'phaser';
import type { NodeType } from '../sim/types';
import { COLORS, nodeStyle } from './palette';
import { drawNodeMark, nodeTypeAccentHex } from './nodeMark';
import { t } from '../i18n';
import { SANS, Panel, uiButton, clean, reducedMotion } from './theme';
import type { StringKey } from '../i18n/en';

/**
 * Cyberpunk-minimal modal that introduces a single node type the first
 * time the player encounters it. Shows: a large animated icon
 * (rotating tick rim, breathing aura), a title in the type's accent
 * color, a short tagline, a bullet list of behaviors, and a single
 * "GOT IT" button that resolves the returned Promise.
 *
 * The popup lives on its own depth (1800+) and dims everything beneath
 * it. Callers gate gameplay on `await NodeIntroPopup.show(scene, type)`.
 */

/**
 * Pull the localized copy for a node type via i18n keys. Returns title,
 * tagline, 3 bullets, and optional note. Note is empty string if there
 * isn't one in the source (en.ts has the canonical key set).
 */
function copyFor(type: NodeType): { title: string; tagline: string; bullets: string[]; note: string } {
  const k = (suffix: string) => `node_${type}_${suffix}` as StringKey;
  // Probe whether a note key exists for this type by comparing the
  // translated result against the raw key — t() returns the key as a
  // fallback when a string is genuinely missing.
  const noteKey = k('note');
  const noteVal = t(noteKey);
  return {
    title: t(k('title')),
    tagline: t(k('tagline')),
    bullets: [t(k('b1')), t(k('b2')), t(k('b3'))],
    note: noteVal === noteKey ? '' : noteVal,
  };
}

/**
 * Builds the popup, plays its entrance, and returns a promise that
 * resolves when the user dismisses it.
 *
 * If you pass `ignoreFor` (typically the main camera so the popup only
 * renders on the UI camera), every Phaser GameObject we create here will
 * be added to the ignore list of that camera.
 */
export class NodeIntroPopup {
  static show(scene: Phaser.Scene, type: NodeType, ignoreFor?: Phaser.Cameras.Scene2D.Camera): Promise<void> {
    return new Promise((resolve) => {
      const { width, height } = scene.scale;
      const calm = reducedMotion();
      const accent = nodeStyle(type).ring;
      const accentBright = nodeStyle(type).ringActive;
      const accentHex = nodeTypeAccentHex(type);

      // Dim is a top-level object at a LOWER depth than the popup container,
      // so the button always wins input. Tap-anywhere-on-dim closes too.
      const dim = scene.add
        .rectangle(0, 0, width, height, 0x000000, 0)
        .setOrigin(0, 0)
        .setScrollFactor(0)
        .setDepth(1750)
        .setInteractive();
      scene.tweens.add({ targets: dim, alpha: 0.82, duration: 220, ease: 'Cubic.easeOut' });
      dim.on('pointerdown', () => close());

      const root = scene.add.container(0, 0).setDepth(1800).setScrollFactor(0);

      // backdrop animated grid lines that radiate outward — sense of importance
      const gridG = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
      root.add(gridG);
      let gridT = 0;
      const drawGrid = () => {
        gridG.clear();
        gridG.lineStyle(2, accent, 0.20);
        const cx = width / 2;
        const cy = height * 0.38;
        const ringCount = 5;
        for (let i = 0; i < ringCount; i++) {
          const t = (gridT + i / ringCount) % 1;
          const r = 90 + t * 750;
          const a = (1 - t) * 0.35;
          gridG.lineStyle(2, accent, a);
          gridG.strokeCircle(cx, cy, r);
        }
      };
      const gridTw = calm ? undefined : scene.tweens.addCounter({
        from: 0, to: 1, duration: 3200, repeat: -1,
        onUpdate: (tw) => { gridT = tw.getValue() ?? 0; drawGrid(); },
      });
      drawGrid();

      // panel card — tall and clean
      const cardW = Math.min(width - 90, 900);
      const cardH = 1360;
      const cardX = width / 2;
      const cardY = height / 2;
      const card = new Panel(scene, cardX - cardW / 2, cardY - cardH / 2, cardW, cardH, {
        fill: 0x0b1226, fillAlpha: 0.99, stroke: accent, strokeAlpha: 0.85,
      }).gfx;
      root.add(card);

      const bx = cardX - cardW / 2;
      const by = cardY - cardH / 2;

      // top eyebrow
      const copy = copyFor(type);
      const eyebrow = scene.add.text(cardX, by + 36, clean(t('popup_new_node')), {
        fontFamily: SANS,
        fontSize: '24px',
        color: '#8d98bd',
        fontStyle: '800',
      }).setOrigin(0.5);
      root.add(eyebrow);

      // === BIG ANIMATED NODE DEMO ===
      const demoY = by + 255;
      const demoRadius = 84;
      const demo = scene.add.container(cardX, demoY);
      root.add(demo);

      const aura = scene.add.circle(0, 0, demoRadius + 60, accent, 0.10)
        .setBlendMode(Phaser.BlendModes.ADD);
      const auraInner = scene.add.circle(0, 0, demoRadius + 30, accent, 0.20)
        .setBlendMode(Phaser.BlendModes.ADD);
      const bg = scene.add.circle(0, 0, demoRadius, nodeStyle(type).fill);
      const ringSoft = scene.add.circle(0, 0, demoRadius + 3, undefined, 0)
        .setStrokeStyle(12, accent, 0.30);
      const ring = scene.add.circle(0, 0, demoRadius, undefined, 0)
        .setStrokeStyle(4, accent, 1);

      const tickRim = scene.add.graphics();
      const drawTicks = (rot: number) => {
        tickRim.clear();
        const r = demoRadius + 18;
        const ticks = 16;
        tickRim.lineStyle(3, accent, 0.55);
        for (let i = 0; i < ticks; i++) {
          const a = rot + (i / ticks) * Math.PI * 2;
          const isLong = i % 4 === 0;
          const inner = r - (isLong ? 12 : 6);
          const outer = r + (isLong ? 3 : 0);
          tickRim.lineBetween(
            Math.cos(a) * inner, Math.sin(a) * inner,
            Math.cos(a) * outer, Math.sin(a) * outer,
          );
        }
      };
      drawTicks(0);
      const tickTw = calm ? undefined : scene.tweens.addCounter({
        from: 0, to: Math.PI * 2, duration: 8000, repeat: -1,
        onUpdate: (tw) => drawTicks(tw.getValue() ?? 0),
      });

      const mark = scene.add.graphics();
      drawNodeMark(mark, type, demoRadius);

      demo.add([aura, auraInner, bg, ringSoft, ring, tickRim, mark]);

      // breathing pulse on the demo
      const breatheTw = calm ? undefined : scene.tweens.add({
        targets: demo,
        scale: { from: 1, to: 1.08 },
        yoyo: true, repeat: -1,
        duration: 1100, ease: 'Sine.easeInOut',
      });

      // periodic "demo pulse" radiating off the node
      const pulseTimer = calm ? undefined : scene.time.addEvent({
        delay: 1200,
        loop: true,
        callback: () => {
          const p = scene.add
            .circle(cardX, demoY, demoRadius, accentBright, 0)
            .setStrokeStyle(3, accentBright, 1)
            .setBlendMode(Phaser.BlendModes.ADD)
            .setDepth(1810);
          root.add(p);
          if (ignoreFor) ignoreFor.ignore(p);
          scene.tweens.add({
            targets: p,
            radius: demoRadius * 2.4,
            alpha: { from: 0.9, to: 0 },
            duration: 1100,
            ease: 'Cubic.easeOut',
            onComplete: () => p.destroy(),
          });
        },
      });

      // === TEXT BLOCK ===
      const titleY = demoY + demoRadius + 105;
      const title = scene.add.text(cardX, titleY, copy.title, {
        fontFamily: SANS,
        fontSize: '72px',
        color: accentHex,
        fontStyle: '800',
      }).setOrigin(0.5);
      root.add(title);

      root.add(scene.add.rectangle(cardX, titleY + 62, 120, 3, accent, 0.7));

      const tagline = scene.add.text(cardX, titleY + 96, copy.tagline, {
        fontFamily: SANS,
        fontSize: '32px',
        color: '#cdd8f5',
        fontStyle: '800',
        wordWrap: { width: cardW - 120 },
        align: 'center',
      }).setOrigin(0.5, 0);
      root.add(tagline);

      let bulletY = titleY + 200;
      for (const b of copy.bullets) {
        const dot = scene.add.text(bx + 54, bulletY, '›', {
          fontFamily: SANS,
          fontSize: '32px',
          color: accentHex,
          fontStyle: '800',
        }).setOrigin(0, 0);
        const t = scene.add.text(bx + 90, bulletY, b, {
          fontFamily: SANS,
          fontSize: '30px',
          color: '#a2b0d8',
          wordWrap: { width: cardW - 150 },
          lineSpacing: 6,
        }).setOrigin(0, 0);
        root.add(dot);
        root.add(t);
        bulletY += Math.max(54, t.height + 18);
      }

      if (copy.note) {
        bulletY += 15;
        const noteBar = scene.add.rectangle(bx + 48, bulletY + 12, 3, 39, accent, 0.65).setOrigin(0, 0);
        const note = scene.add.text(bx + 72, bulletY, copy.note, {
          fontFamily: SANS,
          fontSize: '26px',
          color: '#7a86b0',
          fontStyle: 'italic',
          wordWrap: { width: cardW - 135 },
          lineSpacing: 6,
        }).setOrigin(0, 0);
        root.add(noteBar);
        root.add(note);
      }

      // === DISMISS BUTTON ===
      const btnY = by + cardH - 100;
      const btnC = uiButton(scene, cardX, btnY, cardW - 120, 124, clean(t('got_it')), accent, () => close(), { primary: true, size: 36 });
      root.add(btnC);
      const btn = btnC.list[2] as Phaser.GameObjects.Rectangle;

      // entrance animation
      if (!calm) {
        root.setAlpha(0);
        scene.tweens.add({ targets: root, alpha: 1, duration: 220, ease: 'Cubic.easeOut' });
        card.setScale(0.94);
        scene.tweens.add({ targets: card, scale: 1, duration: 260, ease: 'Cubic.easeOut' });
      }

      // Ensure UI camera routes only — main cam ignores the popup pair.
      if (ignoreFor) {
        ignoreFor.ignore(root);
        ignoreFor.ignore(dim);
      }

      let closing = false;
      // Small stale-tap guard. Just enough to swallow one residual frame
      // from the prior screen — does NOT make the popup feel laggy.
      const mountedAt = scene.time.now;
      const MIN_VISIBLE_MS = 120;
      const close = () => {
        if (closing) return;
        if (scene.time.now - mountedAt < MIN_VISIBLE_MS) return;
        closing = true;
        btn.disableInteractive();
        dim.disableInteractive();
        breatheTw?.remove();
        tickTw?.remove();
        gridTw?.remove();
        pulseTimer?.remove(false);
        scene.tweens.add({
          targets: [root, dim],
          alpha: 0,
          duration: 200,
          ease: 'Cubic.easeIn',
          onComplete: () => {
            root.destroy();
            dim.destroy();
            resolve();
          },
        });
      };
    });
  }
}
